import _ from 'lodash';
import React, { useState, useEffect } from 'react';
import { PropsValue } from 'react-select';
import { format } from 'date-fns';
import { BaseSelect, BaseInput, DatePicker, ISelectOption } from '../../../base';
import { FilterMethod, IColumnFilterCustomProps } from '../filters/filters.models';
import { getFilterMethods, isEmptyValueMethod } from '../filters/filters.service';
import * as S from './author-date-filter.styles';

interface IAuthorDateFilterValue {
    author?: { method: FilterMethod; value: string };
    date?: { method: FilterMethod; value: Date };
}

interface IAuthorDateFilterProps extends IColumnFilterCustomProps {
    value: IAuthorDateFilterValue;
    onChange: (value: IAuthorDateFilterValue) => void;
    filterMethod?: FilterMethod;
    onFilterMethodChange?: (method: FilterMethod) => void;
}

export const AuthorDateFilter: React.FC<IAuthorDateFilterProps> = ({
    value,
    onChange,
}) => {
    const [authorMethod, setAuthorMethod] = useState<FilterMethod>(
        value?.author?.method || FilterMethod.LK
    );
    const [authorValue, setAuthorValue] = useState<string>(value?.author?.value || '');
    const [dateMethod, setDateMethod] = useState<FilterMethod>(
        value?.date?.method || FilterMethod.EQ
    );
    const [dateValue, setDateValue] = useState<Date | undefined>(value?.date?.value);

    // Sync state when value prop changes
    useEffect(() => {
        if (value?.author) {
            setAuthorMethod(value.author.method);
            setAuthorValue(value.author.value);
        }
        if (value?.date) {
            setDateMethod(value.date.method);
            setDateValue(value.date.value);
        }
    }, [value]);

    const authorMethods = getFilterMethods('String');
    const dateMethods = getFilterMethods('DateTime');

    const authorEmptyMethod = isEmptyValueMethod(authorMethod);
    const dateEmptyMethod = isEmptyValueMethod(dateMethod);

    const selectedAuthorMethod = _.find(authorMethods, { value: authorMethod });
    const selectedDateMethod = _.find(dateMethods, { value: dateMethod });

    const handleAuthorMethodChange = (option: PropsValue<ISelectOption>) => {
        if (!option) {
            return;
        }
        const selectedOption = option as ISelectOption;
        const newMethod = selectedOption.value as FilterMethod;
        setAuthorMethod(newMethod);
        
        // Update the combined value
        const newValue: IAuthorDateFilterValue = {
            author: { method: newMethod, value: authorValue },
            date: dateValue ? { method: dateMethod, value: dateValue } : undefined,
        };
        onChange(newValue);
    };

    const handleAuthorValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setAuthorValue(newValue);
        
        // Update the combined value
        const combinedValue: IAuthorDateFilterValue = {
            author: { method: authorMethod, value: newValue },
            date: dateValue ? { method: dateMethod, value: dateValue } : undefined,
        };
        onChange(combinedValue);
    };

    const handleDateMethodChange = (option: PropsValue<ISelectOption>) => {
        if (!option) {
            return;
        }
        const selectedOption = option as ISelectOption;
        const newMethod = selectedOption.value as FilterMethod;
        setDateMethod(newMethod);
        
        // Update the combined value
        const newValue: IAuthorDateFilterValue = {
            author: authorValue ? { method: authorMethod, value: authorValue } : undefined,
            date: { method: newMethod, value: dateValue },
        };
        onChange(newValue);
    };

    const handleDateValueChange = (date: Date | undefined) => {
        const utcDate = date 
            ? new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
            : undefined;
        
        setDateValue(utcDate);
        
        const newValue: IAuthorDateFilterValue = {
            author: authorValue ? { method: authorMethod, value: authorValue } : undefined,
            date: utcDate ? { method: dateMethod, value: utcDate } : undefined,
        };
        onChange(newValue);
    };

    const handleClearAuthor = () => {
        setAuthorValue('');
        setAuthorMethod(FilterMethod.LK);
        
        // Update the combined value without author
        const newValue: IAuthorDateFilterValue = {
            author: undefined,
            date: dateValue ? { method: dateMethod, value: dateValue } : undefined,
        };
        onChange(newValue);
    };

    const handleClearDate = () => {
        setDateValue(undefined);
        setDateMethod(FilterMethod.EQ);
        
        // Update the combined value without date
        const newValue: IAuthorDateFilterValue = {
            author: authorValue ? { method: authorMethod, value: authorValue } : undefined,
            date: undefined,
        };
        onChange(newValue);
    };

    return (
        <S.FilterContainer>
            {/* Temporary disabled author filter */}

            {/* <S.FilterSection>
                <S.SectionHeader>
                    <S.SectionLabel>Author</S.SectionLabel>
                    <S.ClearButton onClick={handleClearAuthor}>Clear</S.ClearButton>
                </S.SectionHeader>
                <S.FilterRow>
                    <BaseSelect
                        menuPosition="fixed"
                        onChange={handleAuthorMethodChange}
                        options={authorMethods}
                        value={selectedAuthorMethod}
                        placeholder="Condition"
                    />
                </S.FilterRow>
                {!authorEmptyMethod && (
                    <S.FilterRow>
                        <BaseInput
                            placeholder="Enter author name..."
                            value={authorValue}
                            onChange={handleAuthorValueChange}
                        />
                    </S.FilterRow>
                )}
            </S.FilterSection>

            <S.Divider /> */}

            <S.FilterSection>
                <S.SectionHeader>
                    <S.SectionLabel>Date</S.SectionLabel>
                    {/* Temporary disabled clear button as we have only one date filter now */}
                    {/* <S.ClearButton onClick={handleClearDate}>Clear</S.ClearButton> */}
                </S.SectionHeader>
                <S.FilterRow>
                    <BaseSelect
                        menuPosition="fixed"
                        onChange={handleDateMethodChange}
                        options={dateMethods}
                        value={selectedDateMethod}
                        placeholder="Condition"
                    />
                </S.FilterRow>
                {!dateEmptyMethod && (
                    <S.FilterRow>
                        <DatePicker
                            value={dateValue ? format(dateValue, 'yyyy-MM-dd') : undefined}
                            onDateSelected={handleDateValueChange}
                            format="yyyy-MM-dd"
                        />
                    </S.FilterRow>
                )}
            </S.FilterSection>
        </S.FilterContainer>
    );
};

